import type { CommunityWorkflowConfiguration, WorkflowType } from '@communities/content-management/domain';
import { useUpsertCommunityWorkflow } from '@communities/content-management/hooks';
import { ErrorBanner } from '@core/components';
import { isDefined } from '@core/utils/type-guards';
import { TextInput, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

import { BillingEstimate } from './BillingEstimate';

// Default budget value
const DEFAULT_MONTHLY_BUDGET = 5000;

interface BillingDialogProps {
  communityId: string;
  workflowType: WorkflowType;
  workflows: CommunityWorkflowConfiguration | null;
  isOpen?: boolean;
  onClose?: () => void;
}

export function BillingDialog({ communityId, workflowType, workflows, isOpen = false, onClose }: BillingDialogProps) {
  const { upsertWorkflow, loading, errors, resetErrors } = useUpsertCommunityWorkflow();
  const [monthlyBudget, setMonthlyBudget] = useState(DEFAULT_MONTHLY_BUDGET);

  const { costPerAudioHour, numberOfClipsPerHour, ...otherApiSettings } = workflows?.apiSettings || {};

  // Sync state when dialog opens or workflows change
  useEffect(() => {
    if (isOpen && workflows) {
      // Use the value from the API or default to 5000
      setMonthlyBudget(workflows.apiSettings?.monthlyBudget || DEFAULT_MONTHLY_BUDGET);
      resetErrors();
    }
  }, [isOpen, workflows, resetErrors]);

  // Handle closing the dialog
  const handleClose = () => {
    if (onClose) {
      onClose();
    }

    resetErrors();
  };

  // Handle monthly budget change
  const handleMonthlyBudgetChange = (value: string) => {
    const parsedValue = parseInt(value, 10) || 0;

    setMonthlyBudget(parsedValue);
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!workflows || monthlyBudget <= 0) {
      return;
    }

    resetErrors();

    // Create updated workflow configuration
    // Note: costPerAudioHour and numberOfClipsPerHour are query-only fields and should not be included in updates
    const updatedWorkflow: CommunityWorkflowConfiguration = {
      ...workflows,
      apiSettings: {
        ...otherApiSettings,
        monthlyBudget,
        ...(otherApiSettings.contentTypeTps && {
          contentTypeTps: {
            ...(isDefined(otherApiSettings.contentTypeTps.text) && { text: otherApiSettings.contentTypeTps.text }),
            ...(isDefined(otherApiSettings.contentTypeTps.image) && { image: otherApiSettings.contentTypeTps.image }),
            ...(isDefined(otherApiSettings.contentTypeTps.audio) && { audio: otherApiSettings.contentTypeTps.audio }),
          },
        }),
      },
    };

    const success = await upsertWorkflow(communityId, workflowType, updatedWorkflow);

    if (success) {
      handleClose();
    }
  };

  const dialogTitle = `${workflowType.charAt(0).toUpperCase() + workflowType.slice(1)} Billing Settings`;
  const dialogDescription = `Configure billing settings for the ${workflowType} workflow`;

  return (
    <Dialog
      description={dialogDescription}
      isOpen={isOpen}
      primaryAction={{
        label: loading ? <LoadingSpinner /> : 'Update Settings',
        onClick: handleSubmit,
        disabled: loading || monthlyBudget <= 0,
      }}
      size="md"
      title={dialogTitle}
      onClose={handleClose}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '25px' }}>
        {/* Error Banner */}
        {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the billing settings:" />}

        <div style={{ padding: '16px 0' }}>
          {/* Monthly Budget Setting */}
          <TextInput
            disabled={loading}
            label="Monthly Budget ($)"
            min="1"
            type="number"
            value={monthlyBudget.toString()}
            onChange={handleMonthlyBudgetChange}
          />

          {/* Estimations */}
          <BillingEstimate clipsPerHour={numberOfClipsPerHour} costPerAudioHour={costPerAudioHour} monthlyBudget={monthlyBudget} />
        </div>
      </div>
    </Dialog>
  );
}
